import { AfterViewInit, Component, ElementRef, inject, Inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { MatSelectChange } from '@angular/material/select';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { Store } from '@ngxs/store';
import { combineLatest, Observable, Subject, Subscription } from 'rxjs';
import { debounceTime, first, map, skip, switchMap } from 'rxjs/operators';
import { RightGroup } from 'src/app/modules/admin/components/admin-roles/local-types/rightGroup';
// eslint-disable-next-line max-len
import { UserEmailTemplateDialogComponent } from 'src/app/modules/customers/components/user-email-template-dialog/user-email-template-dialog.component';
import { CustomerUsersService } from 'src/app/services/customerUsers.service';
import { LaboratoryBasicInfo } from 'src/app/services/dtos/laboratoryBasicInfo';
import { Role } from 'src/app/services/dtos/role';
import { UserEmailTemplate } from 'src/app/services/dtos/userEmailTemplate';
import { UserEmailTemplateForm } from 'src/app/services/dtos/userEmailTemplateForm';
import { RightsService } from 'src/app/services/rights.service';
import { Utils } from 'src/app/shared/common.utils';
import { FetchAllRoles } from 'src/app/states/adminRoles-state/adminRoles.actions';
import { AdminRolesState } from 'src/app/states/adminRoles-state/adminRoles.state';
import { CustomersState } from 'src/app/states/customers-state/customers.state';
import {
    AddCustomerUser,
    AddUser,
    DeleteUserEmailTemplate,
    FetchAllUsersForCustomer,
    GetUserEmailTemplates,
    UpdateCustomerUser,
    UpdateUser
} from 'src/app/states/customerUsers-state/customerUsers.actions';
import { CustomerUsersState } from 'src/app/states/customerUsers-state/customerUsers.state';
import {
    FetchAllLaboratoriesBasicInfo
} from 'src/app/states/laboratories-state/laboratories.actions';
import { LaboratoriesState } from 'src/app/states/laboratories-state/laboratories.state';
import { User } from '../../../services/dtos/user';
import { UserDialogData } from '../../../services/dtos/userDialogData';
import { CustomFormValidators } from '../../customFormValidators';

@Component({
    selector: 'app-user-edit-dialog',
    templateUrl: './user-edit-dialog.component.html',
    styleUrls: ['./user-edit-dialog.component.scss']
})
export class UserEditDialogComponent implements OnInit, AfterViewInit, OnDestroy {
    public selectedCustomerId$: Observable<string> = inject(Store).select(CustomersState.getSelectedCustomerId);
    public users$: Observable<User[]> = inject(Store).select(CustomerUsersState.getUsers);
    public laboratories$: Observable<LaboratoryBasicInfo[]> = inject(Store).select(LaboratoriesState.getLaboratoriesBasicInfo);
    public roles$: Observable<Role[]> = inject(Store).select(AdminRolesState.getRoles);
    public userEmailTemplates$: Observable<UserEmailTemplate[]> = inject(Store).select(CustomerUsersState.getUserEmailTemplates);
    
    @ViewChild(MatSort) public matSort!: MatSort;
    @ViewChild(MatPaginator) public matPaginator!: MatPaginator;
    @ViewChild('laboratorySearch', { static: false }) private laboratorySearch!: ElementRef;
    
    public laboratoriesDictionary!: LaboratoryBasicInfo[];
    public allUsers!: User[];
    public filteredLaboratoriesDictionary!: LaboratoryBasicInfo[];
    public displayingRoles$!: Observable<Role[]>;
    public roles!: Role[];
    public selectedRole!: Role | undefined;
    public userEmailTemplates!: UserEmailTemplate[];
    
    public form!: UntypedFormGroup;
    
    public isAdminPanelMode!: boolean;
    public isAddMode!: boolean;
    public hasPassword!: boolean;
    public hidePassword = true;
    public emailExists = false;
    public currentUsersEmailExists = false;
    public loginNameExists = false;
    public groups!: RightGroup[];
    public userOriginalEmail: string = '';
    
    public templatesDataSource: MatTableDataSource<UserEmailTemplate> = new MatTableDataSource<UserEmailTemplate>();
    public displayedTemplatesColumns: string[] = ['laboratoryEmailTemplateName', 'edit'];
    public selectedEmailId!: string;

    private selectedCustomerId!: string;
    private laboratories!: LaboratoryBasicInfo[];
    private existingUsers!: { isCustomerRole: boolean; emailAddress: string }[];
    private currentUsersEmails!: string[];
    private subs = new Array<Subscription>();
    private passwordPlaceholder = 'pLaCeHoLdEr';
    private verifyEmailSubject$: Subject<{ email: string; isAdminPanel: boolean }> = new Subject();
    private resetEmailFlagsSubject$: Subject<object> = new Subject();

        public constructor(
        private fb: UntypedFormBuilder,
        private store: Store,
        public dialogRef: MatDialogRef<UserEditDialogComponent>,
        @Inject(MAT_DIALOG_DATA) public data: UserDialogData,
        private readonly dialog: MatDialog,
        private readonly accessService: RightsService,
        private readonly userService: CustomerUsersService) {

        this.isAddMode = this.data.isAddMode;
        this.isAdminPanelMode = this.data.isAdminPanelMode;
        this.hasPassword = this.data.user?.hasPassword ?? false;
        this.currentUsersEmails = this.data.currentUsersEmails;

        if (this.isAdminPanelMode) {
            this.form = this.fb.group({
                id: [''],
                name: ['', [Validators.required]],
                eMailAddress: ['', [Validators.required, CustomFormValidators.email]],
                password: [''],
                laboratories: [''],
                hasPassword: [''],
                role: [null, Validators.required],
                isPasswordUpdated: [false],
                loginName: ['']
            });
            if (!this.hasAdminMaintainUsers) {
                this.form.disable();
            }
        }
        else {
            this.form = this.fb.group({
                id: [''],
                name: ['', [Validators.required]],
                eMailAddress: ['', [Validators.required, CustomFormValidators.email]],
                password: ['', Validators.required],
                laboratories: [''],
                hasPassword: [''],
                role: [null, Validators.required],
                isPasswordUpdated: [false],
                loginName: ['', [Validators.required]]
            });
            if (!this.hasMaintainCustomerUsers || !this.hasMaintainCustomer) {
                this.form.disable();
            }
        }
    }

    public get userIsSaved(): boolean {
        return !!this.data?.user?.id;
    }

    public get hasAdminMaintainUsers(): boolean {
        return this.isAdminPanelMode && this.accessService.canMaintainUsers();
    }

    public get hasMaintainCustomerUsers(): boolean {
        return !this.isAdminPanelMode && this.accessService.canMaintainUsersOfCustomers();
    }

    public get hasMaintainCustomer(): boolean {
        return !this.isAdminPanelMode && this.accessService.canMaintainCustomers();
    }

    public get labelForClose(): string {
        return Utils.getLabelForClose(this.hasAdminMaintainUsers || (this.hasMaintainCustomerUsers && this.hasMaintainCustomer));
    }

    public ngOnInit(): void {
        this.store.dispatch(new FetchAllRoles());
        this.store.dispatch(new FetchAllLaboratoriesBasicInfo());
        this.subs.push(this.userEmailTemplates$.subscribe(userEmailTemplates => {
            this.templatesDataSource.data = userEmailTemplates;
            this.templatesDataSource.sort = this.matSort;
            this.userEmailTemplates = userEmailTemplates;
        }));

        this.subs.push(this.selectedCustomerId$.subscribe(customerId => {
            this.selectedCustomerId = customerId;
            this.retrieveUserEmailTemplates(customerId);
        }));

        this.subs.push(this.users$.subscribe(users => {
            this.allUsers = users;
        }));

        this.displayingRoles$ = this.roles$.pipe(skip(1),
            map(((roles: Role[]) => {
                let displayRoles = [] as Role[];

                this.existingUsers = this.data.existingUsers.map(user =>
                ({
                    isCustomerRole: roles.some(role => role.id === user.roleId && role.isCustomerRole),
                    emailAddress: user.emailAddress
                }));

                if (roles) {
                    displayRoles = this.getAvailableRolesForUser(roles);
                    this.setDefaultRoleIfExists(roles);
                }
                return displayRoles;
            })));

        const obs$ = combineLatest([this.laboratories$, this.displayingRoles$])
            .pipe(first(([laboratories, displayingRoles]) => laboratories !== undefined && displayingRoles !== undefined),
                map(([laboratories, roles]) => {
                    this.setUserLaboratories(laboratories, roles);
                }));
        this.subs.push(obs$.subscribe());

        this.subs.push(this.laboratories$.subscribe(laboratories => {
            this.laboratoriesDictionary = laboratories.slice().sort((a, b) => a.name.localeCompare(b.name));
            this.filteredLaboratoriesDictionary = laboratories.slice().sort((a, b) => a.name.localeCompare(b.name));
        }));

        this.addDeleteEmailTemplateColumnOptionIfRightExists();

        this.subs.push(this.verifyEmailSubject$.pipe(
            debounceTime(500),
            switchMap(value => this.userService.verifyAdminUserEmailUse(value))
        ).subscribe(value => this.emailExists = value));
    }

    private addDeleteEmailTemplateColumnOptionIfRightExists(): void {
        if (this.hasAdminMaintainUsers || (this.hasMaintainCustomerUsers && this.hasMaintainCustomer)) {
            this.displayedTemplatesColumns.push('delete');
        }
    }

    private setUserLaboratories(laboratories: LaboratoryBasicInfo[], roles: Role[]): void {
        if (laboratories && roles) {
            if (this.data.user) {
                this.laboratories = laboratories.slice().sort((a, b) => a.name.localeCompare(b.name));
                this.setFormForUser(this.data.user);
            }
        }
    }

    private setDefaultRoleIfExists(roles: Role[]): void {
        if (this.isAdminPanelMode && this.isAddMode) {
            const defaultRole = roles.find(x => x.isDefaultRole && !x.isCustomerRole);
            if (defaultRole) { this.setRole(defaultRole.id); }
        }
        if (!this.isAdminPanelMode && this.isAddMode) {
            const defaultRole = roles.find(x => x.isDefaultRole && x.isCustomerRole);
            if (defaultRole) { this.setRole(defaultRole.id); }
        }
        if (this.selectedRole) {
            this.form.patchValue({ role: this.selectedRole.id });
        }
    }

    private getAvailableRolesForUser(roles: Role[]): Role[] {
        if (!this.isAdminPanelMode) {
            this.roles = roles.filter(x => x.isCustomerRole);
            return this.roles;
        }
        else {
            this.roles = roles.filter(x => !x.isCustomerRole);
            return this.roles;
        }
    }

    public ngAfterViewInit(): void {
        this.templatesDataSource.paginator = this.matPaginator;
        this.subs.push(this.resetEmailFlagsSubject$.subscribe(
            () => { 
                setTimeout(() => {
                    this.emailExists = false;
                    this.currentUsersEmailExists = false;
                    this.form.get('eMailAddress')?.markAsPristine();
                }, 0)
            }
        ));
    }

    public ngOnDestroy(): void {
        this.subs.forEach(x => x.unsubscribe());
    }

    public async verifyEmail(email: string): Promise<void> {  
        if (email && this.existingUsers
            .some(x =>
            x.emailAddress.toLowerCase() === email.trim().toLowerCase() &&
            ((x.isCustomerRole && !this.isAdminPanelMode) || (!x.isCustomerRole && this.isAdminPanelMode)))) {
            if (this.data.user && email === this.data.user.eMailAddress) {
                this.emailExists = false;
            }
            else {
                this.emailExists = true;
            }
        }
        else {
            this.emailExists = false;
        }

        if (email) {
            this.verifyEmailSubject$.next({ email, isAdminPanel: this.isAdminPanelMode });
        }

        this.currentUsersEmailExists = !!email && this.currentUsersEmails.some(item => item.toLowerCase() === email.trim().toLowerCase());
    }

    public getEmailErrorMessage(): string {
        if(!this.isAddMode && this.data.user.eMailAddress === this.userOriginalEmail)
        {
            this.resetEmailFlagsSubject$.next({});
            return '';
        }
        
        return this.currentUsersEmailExists
            ? 'User with provided email already exists on this customer, it cannot be added twice.'
            : !this.isAddMode ? 'User with provided email already exists.'
                : 'User with provided email already exists, user will be added without changes.';
    }

    public verifyLoginName(loginName: string): void {
        if (!loginName || loginName === '') {
            this.loginNameExists = false;
        }
        if (loginName && this.allUsers.some(x => x.loginName === loginName)) {
            if (this.data.user && loginName === this.data.user.loginName) {
                this.loginNameExists = false;
            }
            else {
                this.loginNameExists = true;
            }
        }
        else {
            this.loginNameExists = false;
        }
    }

    public clearPassword(): void {
        const passwordControl = this.form.get('password');
        if (passwordControl?.value === this.passwordPlaceholder) {
            passwordControl?.patchValue('');
        }
    }

    public passwordLostFocus(): void {
        const passwordControl = this.form.get('password');
        if (this.hasPassword && !passwordControl?.value) {
            passwordControl?.patchValue(this.passwordPlaceholder);
            passwordControl?.markAsPristine();
        }
    }

    public isFormControlInvalid(controlName: string): boolean {
        const formControl = this.form.get(controlName);
        if (formControl === null) {
            return false;
        }

        return formControl.invalid && (formControl.dirty || formControl.touched);
    }

    public getErrorMessage(controlName: string): string {
        const formControl = this.form.get(controlName);
        if (formControl === null) {
            return '';
        }
        if (formControl.hasError('required')) {
            return 'You must enter a value';
        }
        if (formControl.hasError('email')) {
            return 'Not a valid email';
        }

        return '';
    }

    public save(): void {
        if (!this.form.untouched && !this.form.pristine && this.canSave()) {
            const roleName = this.roles.find(x => x.id === this.form.get('role')?.value)?.name;
            const isPasswordUpdated = this.form.value.password !== this.passwordPlaceholder;
            const formValue = {
                ...this.form.value,
                isPasswordUpdated,
                laboratories: this.form.value.laboratories === '' ? [] : this.form.value.laboratories,
                eMailAddress: this.form.value.eMailAddress.trim().toLowerCase(),
                roleName,
                customerId: this.selectedCustomerId
            };
            if (this.isAddMode) {
                if (this.isAdminPanelMode) {
                    this.store.dispatch(new AddUser(formValue))
                        .subscribe(() => {
                            this.dialogRef.close();
                        });
                }
                else {
                    this.store.dispatch(new AddCustomerUser(this.selectedCustomerId, formValue))
                        .pipe(first())
                        .subscribe(() => {
                            this.store.dispatch(new FetchAllUsersForCustomer(this.selectedCustomerId));
                            this.dialogRef.close();
                        });
                }
            }
            else {
                if (this.isAdminPanelMode) {
                    this.store.dispatch(new UpdateUser(formValue))
                        .subscribe(() => {
                            this.dialogRef.close();
                        });
                }
                else {
                    this.store.dispatch(new UpdateCustomerUser(formValue))
                        .subscribe(() => {
                            this.store.dispatch(new FetchAllUsersForCustomer(this.selectedCustomerId));
                            this.dialogRef.close();
                        });
                }
            }
        }
    }

    public canSave(): boolean {
        let canSave = true;
        if (this.isAdminPanelMode) {
            canSave = (this.isAddMode && !this.emailExists && !this.currentUsersEmailExists && this.form.valid)
                || (!this.isAddMode && !this.emailExists && !this.currentUsersEmailExists && !this.form.pristine && this.form.valid);
        }
        else {
            canSave = (this.isAddMode && !this.loginNameExists && !this.currentUsersEmailExists && (this.emailExists || this.form.valid ))
                || (!this.isAddMode && !this.emailExists && !this.currentUsersEmailExists
                    && !this.form.pristine && this.form.valid && !this.loginNameExists);
        }
        return canSave;
    }

    public cancel(): void {
        this.dialogRef.close(undefined);
    }

    public setRightGroups(newSelection: MatSelectChange): void {
        this.setRole(newSelection.value);
    }

    public getLaboratoryName(id: string): string {
        const labs = this.laboratoriesDictionary?.filter(laboratory => laboratory.id === id);
        return labs && labs.length > 0 ? labs.slice().sort((a, b) => a.name.localeCompare(b.name))[0].name : '';
    }

    public addNewEmailTemplate(): void {
        let template = new UserEmailTemplateForm();
        template.userId = this.data.user.id;
        template.customerId = this.selectedCustomerId;
        template = {...template, currentEmailTemplates: [...this.userEmailTemplates] };
        const userLaboratories = this.laboratories?.filter(x => this.data.user?.laboratories.includes(x.id));

        this.dialog.open(UserEmailTemplateDialogComponent, {
          width: '48%',
          height: 'i',
          data: { isFromAdminPanel: this.isAdminPanelMode, adminUserLaboratories: userLaboratories, template },
          restoreFocus: false,
          autoFocus: false
        });
      }

    public editEmailTemplate(template: UserEmailTemplateForm): void {
        template = {
            ...template,
            currentEmailTemplates: [...this.userEmailTemplates],
        };
        const userLaboratories = this.laboratories?.filter(x => this.data.user?.laboratories.includes(x.id));

        this.dialog.open(UserEmailTemplateDialogComponent, {
          width: '48%',
          height: 'auto',
          data: { isFromAdminPanel: this.isAdminPanelMode, adminUserLaboratories: userLaboratories, template },
          restoreFocus: false,
          autoFocus: false
        });
      }

    public deleteEmailTemplate(templateId: string): void {
        const shouldDelete = confirm('Are you sure you want to delete email template?');

        if (shouldDelete) {
          this.store.dispatch(new DeleteUserEmailTemplate(templateId, this.data.user.id, this.selectedCustomerId || ''));
        }
      }

    public selectEmailRow(id: string): void {
        this.selectedEmailId = id;
    }

    public filterLaboratories(query: string): void {
        if (!query || query.length < 3) {
          this.filteredLaboratoriesDictionary = this.laboratoriesDictionary;
          return;
        }
    
        this.filteredLaboratoriesDictionary = this.laboratoriesDictionary.filter(x => x.name.toLowerCase().includes(query.toLowerCase()));
    }
    
    public laboratoriesSelectToggled(): void {
    this.filteredLaboratoriesDictionary = this.laboratoriesDictionary;
    this.laboratorySearch.nativeElement.value = '';
    }
    
    public isLaboratoryInFilter(laboratory: LaboratoryBasicInfo): boolean {
    return this.filteredLaboratoriesDictionary.some(x => x.id === laboratory.id);
    }

    private retrieveUserEmailTemplates(customerId: string): void {
        if (!this.isAdminPanelMode && customerId !== '' && this.userIsSaved) {
            this.store.dispatch(new GetUserEmailTemplates(this.data.user.id, this.selectedCustomerId));
        }
        if (this.isAdminPanelMode && this.userIsSaved) {
            this.store.dispatch(new GetUserEmailTemplates(this.data.user.id, this.selectedCustomerId || ''));
        }
    }
    
    private setFormForUser(user: User): void {
        const userLaboratories = this.laboratories?.filter(x => user?.laboratories.includes(x.id)).map(x => x.id);
        const userRole = this.roles?.find(x => x.id === user.role);
        this.userOriginalEmail = user.eMailAddress;

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        this.form.patchValue({
            id: user?.id ?? '',
            name: user?.name ?? '',
            eMailAddress: user?.eMailAddress ?? '',
            hasPassword: this.hasPassword,
            laboratories: userLaboratories ?? '',
            role: userRole?.id ?? '',
            password: this.hasPassword ? this.passwordPlaceholder : '',
            loginName: user?.loginName ?? ''
        });
        if (userRole) {
            this.setRole(userRole.id);
        }

        this.form.markAsUntouched();
        this.form.markAsPristine();
    }
    
    private setRole(id: string): void {
        this.groups = [];
        this.selectedRole = this.roles.find(x => x.id === id);
    }
}
